import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'environments/environment';
import { SESSION_PROVIDER } from '../auth/session-provider';

/**
 * Guards against a burst of parallel 401s each kicking off its own redirect. The first one wins;
 * the rest fall through to the toast only. Reset is unnecessary — a successful `login()` navigates
 * away from the app, and a failed one leaves the user on a page the auth guard will re-challenge.
 */
let reauthInFlight = false;

/**
 * Surfaces auth failures from the Checkins API as one consistent toast, so authorization is a
 * cross-cutting concern handled in a single place rather than re-implemented in every component:
 *   - 403 (the signed-in user lacks the required Checkins role)
 *   - 401 (no session, or the token was rejected/expired)
 *
 * The error is rethrown so components can still drive their own non-toast state (spinners, retry
 * cards). Components suppress their own error toast for these statuses via {@link isAuthError}.
 *
 * A 401 also re-launches the OIDC login. Previously it only toasted: once the refresh token
 * expired, `getValidAccessToken()` returned null, the request went out unauthenticated, the API
 * rejected it, and the user was left on a dead page collecting toasts with no way forward short of
 * a manual reload. Re-challenging here recovers the session and returns the user to where they
 * were — which matters most on a shared check-in tablet mid-event.
 */
export const apiErrorInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  const toastr = inject(ToastrService);
  const session = inject(SESSION_PROVIDER);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 403) {
          toastr.error("You don't have permission to do that.");
        } else if (error.status === 401) {
          toastr.warning('Your session has ended. Signing you back in…');

          if (!reauthInFlight) {
            reauthInFlight = true;
            // Fire-and-forget: login() redirects the document. If it throws (for example
            // sessionStorage was cleared mid-flight) drop the latch so a later request can retry
            // rather than leaving the app permanently unable to re-challenge.
            void session.login(router.url).catch(() => {
              reauthInFlight = false;
            });
          }
        }
      }
      return throwError(() => error);
    }),
  );
};
